// src/ui/steps/step4-festival-loondienst.jsx: Festival loondienst-flow eindscherm
// Toont bruto fulltime + gewogen gemiddeld bruto/maand + per-maand uitsplitsing.
// Geen openingsbod (F2-F.14), geen Word-export (Q.7.5).
import React from "react";
import { FW, FwButton, FwCard, StepHeader, ResultaatSamenvatting } from "../primitives";
import { calc } from "../../lib/calc";
import { Aanname, RolOmschrijvingDetail } from "./step4-result";
import { exportFestivalLoondienst, disclaimerTekst } from "../../lib/docx-export";
import { omschrijvingVoorRol } from "../../data/rollen";

export const StepFestivalLoondienstResult = ({ state, setState, back, restart }) => {
  const huidigeRol = state.rollen?.[0];
  const result = huidigeRol ? calc.berekenFestivalLoondienst(huidigeRol) : null;

  const [copied, setCopied] = React.useState(false);
  const [sharing, setSharing] = React.useState(false);

  if (!huidigeRol || !result) {
    return <div style={{ padding: 40, color: FW.navy }}>Geen berekening mogelijk: ga terug en vul je dienstverband in.</div>;
  }

  const shareLink = () => {
    const json = btoa(unescape(encodeURIComponent(JSON.stringify(state))));
    const url = `${location.origin}${location.pathname}#berekening=${json}`;
    navigator.clipboard.writeText(url);
    setSharing(true);
    setTimeout(() => setSharing(false), 2000);
  };
  const copyEmail = () => {
    const txt = generateEmailFestivalLd(state, huidigeRol, result);
    navigator.clipboard.writeText(txt);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const downloadWord = () => exportFestivalLoondienst({ state, huidigeRol, result });

  return (
    <div className="result-page">
      <StepHeader
        tag="Filmfestival"
        vorm={state.vorm}
        title="Jouw bruto salarisindicatie"
        sub="Op basis van de Fair Pay Salarissen en zzp-starttarieven Filmfestivals en de omvang van jouw dienstverband vind je hier een indicatie van je bruto maandsalaris. Gebruik het als startpunt voor je salarisonderhandeling."
        onBack={back}
      />

      <ResultaatSamenvatting
        isFestival={true}
        vorm={state.vorm}
        rol={huidigeRol.rol}
        dep={huidigeRol.dep}
        niveau={huidigeRol.niveau}
      />

      <div className="row-2col" style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 24, alignItems: "start" }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>Onderbouwing van jouw bruto salarisindicatie</div>
          <div style={{ fontSize: 14, color: FW.navy2, marginBottom: 16 }}>
            Klik op de aparte elementen om te kijken welke onderbouwing je hebt ingevuld of om deze aan te passen.
          </div>
          <div style={{ display: "grid", gap: 8 }}>
            {state.omschrijving && (
              <Aanname
                label="Omschrijving"
                waarde={state.omschrijving.length > 80 ? state.omschrijving.slice(0, 80).trim() + "…" : state.omschrijving}
                uitleg="Dit is de tekst die je hebt ingevuld als omschrijving van de opdracht. De digitale assistent gebruikt deze tekst om een functie en schaal te suggereren."
                onEdit={() => { back(); back(); back(); }}
                detail={
                  <div style={{ background: FW.white, border: `1px solid ${FW.gray100}`, borderRadius: 10, padding: 12, fontSize: 14, color: FW.navy2, lineHeight: 1.55, whiteSpace: "pre-wrap", marginBottom: 12 }}>
                    {state.omschrijving}
                  </div>
                }
              />
            )}
            <Aanname
              label="Type opdracht"
              waarde="Filmfestival: loondienst"
              uitleg="Voor loondienst bij filmfestivals geldt de CAO Toneel & Dans met 12 schalen. Het bruto maandsalaris is een ondergrens; de werkgever mag hoger inschalen."
              onEdit={() => restart()}
            />
            <Aanname
              label="Rol / functie"
              waarde={`${huidigeRol.rol} · ${huidigeRol.dep}`}
              uitleg="Op basis van jouw omschrijving is deze rol gekozen uit het Functiehandboek Referentiefuncties Filmfestivals, of door jou geselecteerd."
              onEdit={() => { back(); back(); back(); }}
              detail={<RolOmschrijvingDetail rol={huidigeRol.rol} dep={huidigeRol.dep} isFestival={true} />}
            />
            <Aanname
              label="Schaal"
              waarde={`Schaal ${huidigeRol.niveau}${result.tariefRij?.rol_voorbeeld ? ` (${result.tariefRij.rol_voorbeeld})` : ""}`}
              uitleg={`Schaal ${huidigeRol.niveau} (CAO Toneel & Dans) bepaalt de salaristabel waaruit jouw bruto fulltime maandsalaris komt.`}
              onEdit={() => { back(); back(); }}
            />
            <Aanname
              label="Periodiek"
              waarde={`Periodiek ${result.periodiek}`}
              uitleg={`Het aantal jaren ervaring binnen schaal ${huidigeRol.niveau}. Elk jaar in dezelfde functie en schaal stijg je één periodiek op tot de top is bereikt.`}
              onEdit={() => back()}
            />
            <Aanname
              label="Dienstverband"
              waarde={`${result.totaalMaanden} maanden in ${result.perBlok.length} period${result.perBlok.length === 1 ? "e" : "es"}`}
              uitleg="Jouw dienstverband is opgesplitst in periodes van maanden × deeltijd-percentage."
              onEdit={() => back()}
            />
          </div>
        </div>

        <div className="aside-card" style={{ display: "grid", gap: 16, position: "sticky", top: 20 }}>
          <FwCard padded={false} style={{ padding: 22, background: FW.cream }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: FW.navy, marginBottom: 10 }}>Goed om te weten</div>
            <ul style={{ fontSize: 14, color: FW.navy2, margin: 0, paddingLeft: 16, lineHeight: 1.55, display: "grid", gap: 8 }}>
              <li>Dit is een <b>ondergrens</b> volgens de Richtlijn zzp-starttarieven en salarissen filmfestivals.</li>
              <li><b>Vakantiegeld, pensioen, vakantiedagen</b> en eventuele eindejaarsuitkering komen <b>bovenop</b>.</li>
              <li>Kijk in de <b>Standaard Basisregeling Filmfestivals (SAV)</b> voor aanvullende afspraken.</li>
              <li>De bruto bedragen gelden bij een <b>38-urige werkweek</b> (fulltime).</li>
            </ul>
          </FwCard>
        </div>
      </div>

      <div className="summary-grid" style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 24, alignItems: "stretch", marginTop: 32 }}>
        <div style={{ background: FW.white, border: `1.5px solid ${FW.gray100}`, borderRadius: 20, padding: 24 }}>
          {result.perBlok.map((b, i) => (
            <div key={b.id || i} style={{ padding: "8px 0" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, fontSize: 15, color: FW.navy, fontVariantNumeric: "tabular-nums" }}>
                <span>
                  Periode {i + 1}: <b>{b.maanden}</b> mnd × <b>{b.pct}%</b> = {calc.fmt(b.brutoMaand)}<span style={{ color: FW.gray500 }}> /mnd</span>
                </span>
                <span style={{ fontWeight: 700, whiteSpace: "nowrap" }}>{calc.fmt(b.brutoBlok)}</span>
              </div>
            </div>
          ))}
          <div style={{ height: 1, background: FW.gray100, margin: "12px 0" }} />
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12,
            padding: "8px 0", fontSize: 17, fontWeight: 700, color: FW.navy, fontVariantNumeric: "tabular-nums",
          }}>
            <span>Totaal periode · {result.totaalMaanden} mnd</span>
            <span style={{ whiteSpace: "nowrap" }}>{calc.fmt(result.totaalBruto)}</span>
          </div>
        </div>

        <FwCard tint="navy" padded={false} className="summary-card" style={{ padding: "32px 28px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <div style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: FW.yellow, opacity: .9 }}>Bruto fulltime</div>
          <div className="summary-value" style={{ fontSize: 48, fontWeight: 700, color: "#fff", lineHeight: 1, marginTop: 8, fontVariantNumeric: "tabular-nums" }}>
            {calc.fmtInt(result.brutoFulltime)}
          </div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,.65)", marginTop: 6 }}>
            Schaal {huidigeRol.niveau} · periodiek {result.periodiek} · /maand
          </div>
        </FwCard>
      </div>

      <FwCard padded={false} style={{ marginTop: 24, padding: 22, background: FW.beige2 }} className="hide-print">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
          <div style={{ minWidth: 0, flex: "1 1 260px" }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: FW.navy, marginBottom: 4, display: "inline-flex", alignItems: "center", gap: 6 }}>
              <i className="ri-send-plane-line" /> Deel je salarisindicatie
            </div>
            <div style={{ fontSize: 14, color: FW.navy2, lineHeight: 1.4 }}>
              Deel via een link, kopieer een gespreksvoorbereiding, of download als pdf.
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <FwButton variant="navy" icon="ri-link" arrow={false} onClick={shareLink}>
              {sharing ? "Link gekopieerd ✓" : "Kopieer deelbare link"}
            </FwButton>
            <FwButton variant="secondary" icon="ri-mail-line" arrow={false} onClick={copyEmail}>
              {copied ? "Tekst gekopieerd ✓" : "Kopieer gespreksvoorbereiding"}
            </FwButton>
            <FwButton variant="secondary" icon="ri-file-word-2-line" arrow={false} onClick={downloadWord}>
              Download als Word
            </FwButton>
          </div>
        </div>
      </FwCard>

      <div style={{ marginTop: 32, display: "flex", justifyContent: "space-between" }} className="hide-print step-nav">
        <FwButton variant="ghost" arrow={false} onClick={back}>← Pas dienstverband aan</FwButton>
        <FwButton variant="secondary" arrow={false} onClick={restart}>Nieuwe berekening</FwButton>
      </div>
    </div>
  );
};

// Kopieer-tekst gespreksvoorbereiding: spiegelt de Word-export
// (exportFestivalLoondienst) volledig, inclusief aandachtspunten.
function generateEmailFestivalLd(state, rol, result) {
  const gemiddeldBrutoMaand = result.totaalMaanden > 0 ? result.totaalBruto / result.totaalMaanden : 0;

  const L = [];
  L.push("Beste [werkgever],", "");
  L.push("Salarisindicatie filmfestival (loondienst)");
  L.push(`Voor mijn inzet als ${rol.rol} (${rol.dep}).`, "");

  L.push("Samenvatting");
  L.push("- Type opdracht: Filmfestival: loondienst");
  L.push(`- Rol · departement: ${rol.rol} · ${rol.dep}`);
  L.push(`- Schaal · periodiek: Schaal ${rol.niveau} · periodiek ${result.periodiek}`);
  L.push(`- Bruto fulltime / maand: ${calc.fmt(result.brutoFulltime)}`);
  L.push(`- Periode: ${result.totaalMaanden} maanden`);
  L.push(`- Gemiddeld bruto / maand: ${calc.fmtInt(gemiddeldBrutoMaand)}`);
  L.push(`- Totaal periode: ${calc.fmtInt(result.totaalBruto)}`, "");

  L.push(`Rol: ${rol.rol} · ${rol.dep}`);
  L.push("Op basis van jouw keuze uit het Functiehandboek Referentiefuncties Filmfestivals.");
  const omschrijving = omschrijvingVoorRol("festival", rol.dep, rol.rol);
  if (omschrijving) L.push(omschrijving.split(/\n{2,}/).map(b => b.replace(/\n/g, " ").trim()).join("\n"));
  L.push("Zie voor het uitgebreide profiel van de Referentiefunctie het Functiehandboek Referentiefuncties Filmfestivals.", "");

  L.push("Onderbouwing per dienstverband-blok");
  result.perBlok.forEach(b => L.push(`${b.maanden} maanden × ${b.pct}% van fulltime = ${calc.fmt(b.brutoMaand)} bruto/maand`));
  L.push(`Totaal periode (${result.totaalMaanden} mnd): ${calc.fmt(result.totaalBruto)}`);
  L.push(`Gemiddeld /maand (gewogen): ${calc.fmt(gemiddeldBrutoMaand)}`, "");

  if (state.omschrijving) L.push("Omschrijving van de opdracht", state.omschrijving, "");

  L.push("Aandachtspunten voor het gesprek");
  L.push("Dit bruto-bedrag is de ondergrens. In een loondienst-gesprek gaat het verder over de secundaire voorwaarden:");
  L.push("- Vakantiegeld (8%) en eindejaarsuitkering: meestal bovenop het bruto.");
  L.push("- Pensioenbijdrage werkgever: vraag naar de regeling en de werkgeversbijdrage.");
  L.push("- Compensatie in tijd voor festivaldagen, weekend- en avondwerk.");
  L.push("- Reiskosten en eventueel thuiswerkvergoeding.");
  L.push("- Vertrouwenspersoon en sociale veiligheid: moet bij het festival belegd zijn.", "");

  L.push("Goed om te benoemen");
  L.push("- Dit is de ondergrens op basis van de Salaristabel Filmfestivals en het Functiehandboek Filmfestivals. De werkgever mag hoger inschalen op basis van ervaring of complexiteit.");
  L.push("- Vakantiegeld, pensioen, vakantiedagen en eventuele eindejaarsuitkering komen bovenop dit bruto bedrag.");
  L.push("- Bij variabele inzet per maand kun je vragen om compensatie in tijd of een vaste opslag voor onregelmatige uren.", "");

  L.push(disclaimerTekst("filmfestivals"), "");
  L.push("Met vriendelijke groet,", "[jouw naam]");

  return L.join("\n");
}
